/**
 * Represents operators for string-based filters.
 *
 * - `=`: Equal to
 * - `:`: Contains
 * - `!=`: Not equal to
 * - `:=`: Exact match
 */
type StringOperator = '=' | ':' | '!=' | ':=';

/**
 * Represents operators for numeric-based filters, including range.
 *
 * - `>`: Greater than
 * - `<`: Less than
 * - `>=`: Greater than or equal to
 * - `<=`: Less than or equal to
 * - `[min..max]`: Range between min and max
 */
type NumericOperator = '>' | '<' | '>=' | '<=' | '[min..max]';

/**
 * Represents logical operators for combining filters.
 *
 * - `&&`: Logical AND
 * - `||`: Logical OR
 */
type LogicalOperator = '&&' | '||';

/**
 * Union type for all possible filter operators.
 */
type FilterOperator = StringOperator | NumericOperator | LogicalOperator;

/**
 * Creates a filter string for Typesense queries.
 *
 * @template T - The type of the field, extending string.
 * @param {T} field - The field to filter on.
 * @param {FilterOperator} operator - The operator to use for filtering.
 * @param {string | number | [number, number] | string[]} value - The value to filter by.
 * @returns {string} The constructed filter string.
 *
 * @throws Will throw an error if a range filter is used with an invalid value.
 */
export function createFilterByString<T extends string>(
  field: T,
  operator: FilterOperator,
  value: string | number | [number, number] | string[],
): string {
  if (Array.isArray(value)) {
    // Handle array values
    return `${field}:${operator}:[${value.join(',')}]`;
  } else if (operator === '[min..max]') {
    // Handle range values
    if (!Array.isArray(value) || value.length !== 2) {
      throw new Error('Range filter requires an array of [min, max] values');
    }
    const [min, max] = value;
    return `${field}:${operator.replace('min', min.toString()).replace('max', max.toString())}`;
  } else {
    // Handle string or number values
    return `${field}:${operator}:${value}`;
  }
}
